/**
 * Admin Engagement Page
 * Contractor -> client outreach across every medium on Eqaya: quotations,
 * site-visit (assessment) requests, scheduled visits/calls, quote-request
 * responses, and chat activity. Answers "are contractors reaching out, and
 * where?" without a database session.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Box, Typography, Breadcrumbs, Link, Card, CardContent,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, ToggleButton,
  ToggleButtonGroup, CircularProgress, Alert, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Divider
} from '@mui/material';
import {
  NavigateNext, RequestQuote, EventAvailable, PhoneInTalk, ReplyAll, WhatsApp,
  ChatBubbleOutline, Forum
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { renderMarkdown } from '../components/documents/documentStyles';

const fmtWhen = (d) => d ? new Date(d).toLocaleString('en-GB', {
  timeZone: 'Africa/Harare', day: 'numeric', month: 'short',
  hour: '2-digit', minute: '2-digit'
}) : '—';

const fmtMoney = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${currency && currency !== 'USD' ? currency + ' ' : '$'}${n.toLocaleString('en-US')}`;
};

const statusColor = (s) => ({
  accepted: 'success', confirmed: 'success', completed: 'success',
  submitted: 'info', responded: 'info', pending: 'warning',
  withdrawn: 'default', declined: 'error', rejected: 'error'
}[String(s || '').toLowerCase()] || 'default');

// Owner preference: identify contractors by their company; person name is the fallback.
const contractorLabel = (row) => row.contractor_company || row.contractor_name || '—';

// Where the client is based (their profile, not the property): city/district +
// province, with a country chip when they're outside Zimbabwe (diaspora signal).
const clientBase = (row) => {
  const parts = [];
  const place = row.client_city || row.client_district;
  if (place) parts.push(place);
  if (row.client_province && row.client_province !== place) parts.push(row.client_province);
  return parts.join(', ');
};

const Section = ({ icon, title, subtitle, count, columns, rows, renderRow, emptyText }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Chip size="small" label={count} color={count > 0 ? 'primary' : 'default'} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{emptyText}</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => <TableCell key={c} sx={{ fontWeight: 600 }}>{c}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>{rows.map(renderRow)}</TableBody>
          </Table>
        </Box>
      )}
    </CardContent>
  </Card>
);

// `embedded` renders without breadcrumbs/outer container — used as a tab
// inside AdminDashboard; the standalone /admin/engagement route also lands there.
const AdminEngagementPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [funnel, setFunnel] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/engagement?days=${d}`);
      setData(res.data.data);
      try {
        const f = await api.get(`/admin/assessment-requests/funnel?days=${d}`);
        setFunnel(f.data?.data || null);
      } catch (_) { setFunnel(null); }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  // Click-through views: quotation detail + read-only chat transcript
  const [quoteDialog, setQuoteDialog] = useState({ open: false, loading: false, bid: null });
  const [chatDialog, setChatDialog] = useState({ open: false, loading: false, title: '', messages: [], contractorId: null });

  const openQuote = async (bidId) => {
    setQuoteDialog({ open: true, loading: true, bid: null });
    try {
      const res = await api.get(`/admin/engagement/bids/${bidId}`);
      setQuoteDialog({ open: true, loading: false, bid: res.data.data });
    } catch (e) {
      setQuoteDialog({ open: true, loading: false, bid: null });
    }
  };

  const openChat = async (row) => {
    const title = `${contractorLabel(row)} ↔ ${row.client_name || 'client'}`;
    setChatDialog({ open: true, loading: true, title, messages: [], contractorId: null });
    try {
      const res = await api.get(`/admin/engagement/conversations/${row.id}/messages`);
      setChatDialog({ open: true, loading: false, title, messages: res.data.data || [], contractorId: null });
    } catch (e) {
      setChatDialog({ open: true, loading: false, title, messages: [], contractorId: null });
    }
  };

  const t = data?.totals || {};
  const anyActivity = Object.values(t).some((n) => n > 0);

  return (
    <Container maxWidth="lg" sx={embedded ? { py: 0, px: 0 } : { py: 3 }} disableGutters={embedded}>
      {!embedded && (
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/dashboard')}>Admin</Link>
          <Typography color="text.primary">Engagement</Typography>
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Engagement</Typography>
          <Typography variant="body2" color="text.secondary">
            Contractors reaching out to clients — every medium on Eqaya, times in Zimbabwe time
          </Typography>
        </Box>
        <ToggleButtonGroup value={days} exclusive size="small"
          onChange={(_, v) => v && setDays(v)}>
          <ToggleButton value={7}>7 days</ToggleButton>
          <ToggleButton value={14}>14 days</ToggleButton>
          <ToggleButton value={30}>30 days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {!loading && !error && data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3, mt: 0.5 }}>
            {[
              ['Quotations', t.bids],
              ['Visit requests', t.assessments],
              ['Visits/calls', t.visits],
              ['Quote responses', t.quoteResponses],
              ['New chats', t.newChats],
              ['Active threads', t.activeThreads]
            ].map(([label, n]) => (
              <Grid item xs={6} sm={4} md={2} key={label}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{n ?? 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {!anyActivity && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No contractor outreach in the last {data.days} days. Consider a nudge — the daily
              new-projects digest goes out at 18:00, or run a manual blast.
            </Alert>
          )}

          {funnel && (
            <Section
              icon={<EventAvailable color="primary" />}
              title="Visit funnel" count={funnel.total?.requested || 0}
              subtitle={`Requested → replied → scheduled → completed → quoted → accepted, per contractor. ${funnel.total?.expired || 0} expired unanswered${funnel.declineReasons?.length ? ` · reasons given: ${funnel.declineReasons.map((r) => `${r.label} ×${r.n}`).join(', ')}` : ''}`}
              columns={['Contractor', 'Requested', 'Replied', 'Declined', 'Scheduled', 'Completed', 'Wants quote', 'Quoted', 'Accepted']}
              rows={funnel.byContractor || []} emptyText="No visit requests in this window."
              renderRow={(r) => (
                <TableRow key={r.contractor} hover>
                  <TableCell>{r.contractor}</TableCell>
                  <TableCell>{r.requested}</TableCell>
                  <TableCell>{r.replied} <Typography variant="caption" color="text.secondary">({r.requested ? Math.round((r.replied / r.requested) * 100) : 0}%)</Typography></TableCell>
                  <TableCell>{r.declined}</TableCell>
                  <TableCell>{r.scheduled}</TableCell>
                  <TableCell>{r.completed}{r.did_not_happen > 0 && <Chip size="small" color="error" label={`${r.did_not_happen} disputed`} sx={{ ml: 0.5 }} />}</TableCell>
                  <TableCell>{r.wants_quote}</TableCell>
                  <TableCell>{r.quoted}</TableCell>
                  <TableCell>{r.accepted}</TableCell>
                </TableRow>
              )}
            />
          )}

          {data.responsiveness && (
            <Card sx={{ mb: 3, bgcolor: 'rgba(67, 181, 129, 0.04)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Client responsiveness</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  How often — and how fast — clients answer what contractors send them in this window
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ['Overall response rate', data.responsiveness.overall,
                      `${data.responsiveness.overall.responded} of ${data.responsiveness.overall.total} items answered`],
                    ['Visit requests', data.responsiveness.assessments,
                      data.responsiveness.assessments.medianHours != null ? `median ${data.responsiveness.assessments.medianHours}h to respond` : 'no responses yet'],
                    ['Quotations', data.responsiveness.bids,
                      data.responsiveness.bids.medianHours != null ? `median ${data.responsiveness.bids.medianHours}h to decide` : 'no decisions yet'],
                    ['Chats', data.responsiveness.chats,
                      data.responsiveness.chats.medianHours != null ? `median ${data.responsiveness.chats.medianHours}h to first reply` : 'no replies yet']
                  ].map(([label, stat, sub]) => (
                    <Grid item xs={6} md={3} key={label}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="h5" sx={{
                            fontWeight: 700,
                            color: stat.rate == null ? 'text.disabled' : stat.rate >= 60 ? 'success.main' : stat.rate >= 30 ? 'warning.main' : 'error.main'
                          }}>
                            {stat.rate == null ? '—' : `${stat.rate}%`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                          <Typography variant="caption" color="text.secondary">{sub}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {data.whatsapp && (
            <Section
              icon={<WhatsApp sx={{ color: '#25D366' }} />}
              title="WhatsApp adoption" count={data.whatsapp.linked}
              subtitle={`${data.whatsapp.active} linked (${data.whatsapp.optedOut} opted out) · ${data.whatsapp.activeInWindow} active in this window · ${data.whatsapp.messagesInWindow} messages relayed`}
              columns={['User', 'Role', 'WhatsApp number', 'Status', 'Linked', 'Last used', 'Sent / received']}
              rows={data.whatsapp.users} emptyText="Nobody has linked WhatsApp yet."
              renderRow={(w) => (
                <TableRow key={w.user_id} hover>
                  <TableCell>
                    {w.company_name || w.name || '—'}
                    <Typography variant="caption" color="text.secondary" display="block">{w.email}</Typography>
                  </TableCell>
                  <TableCell>{w.role}</TableCell>
                  <TableCell>+{w.wa_id}</TableCell>
                  <TableCell>
                    <Chip size="small" label={w.status === 'blocked' ? 'opted out' : w.status}
                      color={w.status === 'active' ? 'success' : w.status === 'blocked' ? 'error' : 'default'} />
                  </TableCell>
                  <TableCell>{fmtWhen(w.linked_at)}</TableCell>
                  <TableCell>{fmtWhen(w.last_inbound_at)}</TableCell>
                  <TableCell>
                    {w.msgs_sent} / {w.msgs_received}
                    {(w.msgs_sent > 0 || w.msgs_received > 0) &&
                      <Chip size="small" label="active" color="success" sx={{ ml: 0.5 }} />}
                  </TableCell>
                </TableRow>
              )}
            />
          )}

          <Section
            icon={<RequestQuote color="primary" />}
            title="Quotations submitted" count={t.bids}
            subtitle="Formal quotations contractors sent on projects"
            columns={['When', 'Contractor', 'Client', 'Project', 'Amount', 'Status']}
            rows={data.bids} emptyText="No quotations in this window."
            renderRow={(b) => (
              <TableRow key={b.id} hover onClick={() => openQuote(b.id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{fmtWhen(b.created_at)}</TableCell>
                <TableCell>{contractorLabel(b)}</TableCell>
                <TableCell>{b.client_name || '—'}</TableCell>
                <TableCell>{b.project_title || b.project_id}</TableCell>
                <TableCell>{fmtMoney(b.total_cost, b.currency)}</TableCell>
                <TableCell><Chip size="small" label={b.status || '—'} color={statusColor(b.status)} /></TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<EventAvailable color="primary" />}
            title="Site-visit requests" count={t.assessments}
            subtitle="Contractors asking to assess a property before quoting"
            columns={['When', 'Contractor', 'Client', 'Client based in', 'Project', 'Status']}
            rows={data.assessments} emptyText="No site-visit requests in this window."
            renderRow={(a) => (
              <TableRow key={a.id} hover>
                <TableCell>{fmtWhen(a.created_at)}</TableCell>
                <TableCell>{contractorLabel(a)}</TableCell>
                <TableCell>{a.client_name || '—'}</TableCell>
                <TableCell>
                  {clientBase(a) || '—'}
                  {a.client_country && a.client_country !== 'ZW' && (
                    <Chip size="small" label={a.client_country} color="warning" sx={{ ml: 0.5 }} />
                  )}
                </TableCell>
                <TableCell>{a.project_title || a.project_id}</TableCell>
                <TableCell>
                  <Chip size="small" label={a.status || '—'} color={statusColor(a.status)} />
                  {a.accepted_window
                    ? <Chip size="small" label="visit confirmed" color="success" sx={{ ml: 0.5 }} />
                    : (!a.owner_responded_at && <Chip size="small" label="awaiting owner" color="warning" sx={{ ml: 0.5 }} />)}
                </TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<PhoneInTalk color="primary" />}
            title="Visits & calls scheduled" count={t.visits}
            subtitle="Site visits and calls booked with clients"
            columns={['Booked', 'Type', 'Client', 'Project', 'Scheduled for', 'Status']}
            rows={data.visits} emptyText="No visits or calls in this window."
            renderRow={(v) => (
              <TableRow key={v.id} hover>
                <TableCell>{fmtWhen(v.created_at)}</TableCell>
                <TableCell>{(v.visit_type || 'visit').replace(/_/g, ' ')}</TableCell>
                <TableCell>{v.client_name || '—'}</TableCell>
                <TableCell>{v.project_title || v.project_id}</TableCell>
                <TableCell>{fmtWhen(v.scheduled_date)}</TableCell>
                <TableCell><Chip size="small" label={v.status || '—'} color={statusColor(v.status)} /></TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<ReplyAll color="primary" />}
            title="Quote-request responses" count={t.quoteResponses}
            subtitle="Contractors answering quotation requests sent by clients"
            columns={['When', 'Contractor', 'Client', 'Request', 'Amount', 'Status']}
            rows={data.quoteResponses} emptyText="No responses in this window."
            renderRow={(q) => (
              <TableRow key={q.id} hover>
                <TableCell>{fmtWhen(q.responded_at)}</TableCell>
                <TableCell>{q.contractor_name || '—'}</TableCell>
                <TableCell>{q.client_name || '—'}</TableCell>
                <TableCell>{(q.project_description || '—').slice(0, 60)}</TableCell>
                <TableCell>{fmtMoney(q.quote_amount, q.quote_currency)}</TableCell>
                <TableCell><Chip size="small" label={q.status || '—'} color={statusColor(q.status)} /></TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<ChatBubbleOutline color="primary" />}
            title="New chats started by contractors" count={t.newChats}
            subtitle="Conversations where the contractor sent the first message"
            columns={['Started', 'Contractor', 'Client', 'Project', 'Messages']}
            rows={data.newChats} emptyText="No contractor-initiated chats in this window."
            renderRow={(c) => (
              <TableRow key={c.id} hover onClick={() => openChat(c)} sx={{ cursor: 'pointer' }}>
                <TableCell>{fmtWhen(c.created_at)}</TableCell>
                <TableCell>{contractorLabel(c)}</TableCell>
                <TableCell>{c.client_name || '—'}</TableCell>
                <TableCell>{c.project_title || '—'}</TableCell>
                <TableCell>{c.msg_count}</TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<Forum color="primary" />}
            title="Threads with contractor messages" count={t.activeThreads}
            subtitle="Any conversation where a contractor wrote to a client in this window"
            columns={['Last message', 'Contractor', 'Client', 'Project', 'Messages sent']}
            rows={data.activeThreads} emptyText="No contractor messages in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover onClick={() => openChat(r)} sx={{ cursor: 'pointer' }}>
                <TableCell>{fmtWhen(r.last_at)}</TableCell>
                <TableCell>{contractorLabel(r)}</TableCell>
                <TableCell>{r.client_name || '—'}</TableCell>
                <TableCell>{r.project_title || '—'}</TableCell>
                <TableCell>{r.msg_count}</TableCell>
              </TableRow>
            )}
          />
        </>
      )}

      {/* Quotation detail */}
      <Dialog open={quoteDialog.open} onClose={() => setQuoteDialog({ open: false, loading: false, bid: null })} maxWidth="md" fullWidth>
        <DialogTitle>Quotation{quoteDialog.bid ? ` — ${quoteDialog.bid.project_title || quoteDialog.bid.project_id}` : ''}</DialogTitle>
        <DialogContent dividers>
          {quoteDialog.loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
          {!quoteDialog.loading && !quoteDialog.bid && (
            <Typography color="text.secondary">Couldn't load this quotation.</Typography>
          )}
          {!quoteDialog.loading && quoteDialog.bid && (() => {
            const b = quoteDialog.bid;
            let breakdown = b.cost_breakdown;
            if (typeof breakdown === 'string') { try { breakdown = JSON.parse(breakdown); } catch { breakdown = null; } }
            const lineItems = Array.isArray(breakdown?.lineItems) ? breakdown.lineItems : null;
            return (
              <Box>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Contractor</Typography>
                    <Typography>{b.contractor_company || b.contractor_name || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Client</Typography>
                    <Typography>{b.client_name || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{fmtMoney(b.total_cost, b.currency)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box><Chip size="small" label={b.status || '—'} color={statusColor(b.status)} /></Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography>{b.estimated_duration || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Submitted</Typography>
                    <Typography>{fmtWhen(b.created_at)}</Typography>
                  </Grid>
                </Grid>
                {b.executive_summary && (<>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2">Summary</Typography>
                  <Box>{renderMarkdown(b.executive_summary)}</Box>
                </>)}
                {b.scope_of_work && (<>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2">Scope of work</Typography>
                  <Box>{renderMarkdown(b.scope_of_work)}</Box>
                </>)}
                {lineItems && lineItems.length > 0 && (<>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Cost breakdown</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Rate</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {lineItems.map((li, i) => (
                          <TableRow key={i}>
                            <TableCell>{li.description || li.item || '—'}</TableCell>
                            <TableCell>{li.quantity ?? '—'}{li.unit ? ` ${li.unit}` : ''}</TableCell>
                            <TableCell>{fmtMoney(li.rate ?? li.unitPrice, b.currency)}</TableCell>
                            <TableCell>{fmtMoney(li.total ?? (Number(li.quantity) * Number(li.rate ?? li.unitPrice)), b.currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>)}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDialog({ open: false, loading: false, bid: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Chat transcript (read-only) */}
      <Dialog open={chatDialog.open} onClose={() => setChatDialog({ open: false, loading: false, title: '', messages: [], contractorId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Conversation — {chatDialog.title}</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          {chatDialog.loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
          {!chatDialog.loading && chatDialog.messages.length === 0 && (
            <Typography color="text.secondary">No messages to show.</Typography>
          )}
          {!chatDialog.loading && chatDialog.messages.map((m) => (
            <Box key={m.id} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {(m.sender_role === 'contractor' ? (m.sender_company || m.sender_name) : m.sender_name) || 'Unknown'}
                {m.sender_role ? ` (${m.sender_role.replace(/_/g, ' ')})` : ''} · {fmtWhen(m.created_at)}
              </Typography>
              <Box sx={{ bgcolor: m.sender_role === 'contractor' ? 'primary.50' : 'background.paper',
                border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.2, mt: 0.3 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                {m.attachment_url && (
                  <Typography variant="caption">
                    <a href={m.attachment_url} target="_blank" rel="noreferrer">📎 {m.attachment_name || 'Attachment'}</a>
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChatDialog({ open: false, loading: false, title: '', messages: [], contractorId: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEngagementPage;
